<template>
  <div class="user-scope">
    <div
      v-if="!hideOp"
      class="_op"
    >
      <m-select
        v-model:value="selectedType"
        :items="operatorTypes"
        :read-only="readOnly"
        :disabled="disabled"
        class="_select"
      />
    </div>
    <user-picker
      v-if="!isEmpty"
      v-model:value="selectedUsers"
      class="_user-select"
      full-width
      nowrap
      :truncate-selected-items="maxTags"
      :read-only="readOnly"
      :disabled="disabled"
      :max-tag-text-length="maxTagTextLength"
      :placeholder="$t('userScope.placeholder')"
    />
  </div>
</template>

<script setup>
import UserPicker from '@/components/UserPicker.vue';
import useScopeItem from '@/composables/filter/scope-item';
import { AND, EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { propertyType } from 'shared/constants.json';

const props = defineProps({
  value: {
    type: Object,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  isNot: {
    type: Boolean,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideOp: {
    type: Boolean,
    default: false,
  },
  maxTagTextLength: {
    type: Number,
    default: 0,
  },
  hideIsEmpty: {
    type: Boolean,
    default: false,
  },
  maxTags: {
    type: Number,
    default: 2,
  },
});

const emit = defineEmits(['input', 'update:value', 'change-not', 'change']);

const selectedUsers = ref([]);
const isEmpty = ref(false);

const { operatorTypes: getOperatorTypes } = useScopeItem();
const operatorTypes = computed(() => getOperatorTypes(propertyType.user, { hideEmpty: props.hideIsEmpty }));

const selectedType = computed({
  get() {
    if (props.isNot) {
      if (isEmpty.value) {
        return NOT_EMPTY;
      }
      return NOT;
    }
    if (isEmpty.value) {
      return EMPTY;
    }
    return AND;
  },
  set(val) {
    updateEmpty(val);
    if ([NOT, NOT_EMPTY].includes(val)) {
      emit('change-not', NOT);
      return;
    }
    emit('change-not', AND);
  },
});

const scope = computed(() => ({
  ...props.value,
  [props.fieldName]: selectedUsers.value,
  isEmpty: isEmpty.value,
}));

const updateEmpty = (type) => {
  if ([EMPTY, NOT_EMPTY].includes(type)) {
    isEmpty.value = true;
    return;
  }
  isEmpty.value = false;
};

const updateValues = () => {
  let isEmptyValue = false;
  if (props.value !== null && typeof props.value.isEmpty !== 'undefined') {
    isEmptyValue = props.value.isEmpty;
  }
  isEmpty.value = isEmptyValue;

  if (props.value === null || isNullOrUndefined(props.value[props.fieldName])) {
    selectedUsers.value = [];
    return;
  }
  selectedUsers.value = props.value[props.fieldName];
};

const emitUpdate = (val) => {
  emit('input', val);
  emit('update:value', val);
  emit('change', val);
};

updateValues();

watch(scope, (newVal, oldVal) => {
  if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
    return;
  }

  const cp = copy(newVal);
  emitUpdate(cp);
}, { deep: true });

watch(() => props.value, () => {
  updateValues();
}, { deep: true });
</script>

<style scoped lang="scss" type="text/scss">
  .user-scope {
    display: flex;

    ._op {
      margin-right: .8rem;

      ._select {
        width: 100%;
      }
    }

    ._user-select {
      min-width: 20rem;
    }
  }
</style>
